import React from 'react';
import { View, Text, StyleSheet, Pressable, Image, Platform } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { strings } from '@/constants/residents/strings';
import { LoginForm } from '@/features/auth/LoginForm';
import { useLoginForm } from '@/features/auth/useLoginForm';
import { ResioLogo } from '@/assets/ResioLogo';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import type { ThemeContextValue } from '@/hooks/shared/useThemeColors';
import { AuthHeader } from '@/components/auth/AuthHeader';
import { useAppSelector } from '@/store';
import { useResponsive } from '@/hooks/common/useResponsive';
import { useLegalContent } from '@/hooks/common/useLegalContent';
import { LegalModal } from '@/components/common/modals';
import { AuthStackParamList } from '@/navigation/residents/mobile/AuthNavigator';

export const LoginScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const state = useLoginForm();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const { isMobile, isTablet, isDesktop, isLarge, width } = useResponsive();
  const { showLegalContent, modalVisible, modalContentType, closeModal } = useLegalContent();
  const styles = useThemedStyles(makeStyles);

  const onTermsOfService = () => showLegalContent('terms');
  const onPrivacyPolicy = () => showLegalContent('privacy');

  const title = state.isAdminMode ? 'Welcome to Resio Community Portal!' : 'Welcome to Resio!';
  // const subtitle = state.isAdminMode ? undefined : undefined;
  const image = state.isAdminMode ? require('@/assets/images/login.png') : require('@/assets/images/sign-up.png');

  // Dynamic styles based on admin mode and responsive breakpoints
  const dynamicStyles = {
    pageWrapper: [
      styles.pageWrapper,
      state.isAdminMode ? styles.pageWrapperAdmin : styles.pageWrapperResident
    ],
    splitContainer: [
      styles.splitContainer,
      isMobile && styles.splitContainerMobile,
      isTablet && styles.splitContainerTablet,
      isDesktop && styles.splitContainerDesktop
    ],
    imagePane: [
      styles.imagePane,
      isMobile && styles.imagePaneMobile,
      isTablet && styles.imagePaneTablet,
      isDesktop && styles.imagePaneDesktop
    ],
    formPane: [
      styles.formPane,
      state.isAdminMode ? styles.formPaneAdmin : styles.formPaneResident,
      isMobile && styles.formPaneMobile,
      isTablet && styles.formPaneTablet,
      isDesktop && styles.formPaneDesktop
    ],
    formInner: [
      styles.formInner,
      isMobile && styles.formInnerMobile,
      isTablet && styles.formInnerTablet,
      isDesktop && styles.formInnerDesktop
    ],
    title: [
      styles.title,
      state.isAdminMode ? styles.titleAdmin : styles.titleResident,
      isMobile && styles.titleMobile,
      isTablet && styles.titleTablet,
      isDesktop && styles.titleDesktop
    ],
    signupRow: [
      styles.signupRow,
      state.isAdminMode ? styles.signupRowAdmin : styles.signupRowResident,
      isMobile && styles.signupRowMobile
    ],
    link: [
      styles.link,
      state.isAdminMode ? styles.linkAdmin : styles.linkResident
    ],
    // New style for conditional content container
    conditionalContent: [
      styles.conditionalContent,
      !state.isAdminMode && styles.conditionalContentVisible
    ]
  };

  return (
    <View style={dynamicStyles.pageWrapper}>
      {/* Header with Resio Logo - Only show for unauthenticated users or mobile */}
      <AuthHeader show={!isAuthenticated || Platform.OS !== 'web'} />

      <View style={dynamicStyles.splitContainer}>
        {/* Left side - Image pane (hidden on mobile, visible on desktop) */}
        <View style={dynamicStyles.imagePane}>
          <Image
            source={image}
            style={styles.image}
            resizeMode="cover"
          />
        </View>
        
        {/* Right side - Form pane */}
        <View style={dynamicStyles.formPane}>
          <View style={dynamicStyles.formInner}>
            {/* Title - Fixed height container to prevent shift */}
            <View style={styles.titleContainer}>
              <Text style={dynamicStyles.title}>{title}</Text>
            </View>

            {/* Login Form */}
            <LoginForm state={state} autoFocusEmail />

            {/* Links Row - Forgotten Password */}
            <View style={styles.linksRow}>
              <Text style={styles.spacer} />
              <Pressable onPress={() => {
                navigation.navigate('ForgotPassword', { email: state.email });
              }}>
                <Text style={dynamicStyles.link}>Forgotten Password?</Text>
              </Pressable>
            </View>

            {/* Conditional content container - Always reserves space to prevent shift */}
            <View style={dynamicStyles.conditionalContent}>
              {/* Sign up link - Only show for residents */}
              <Text style={[dynamicStyles.signupRow, !state.isAdminMode ? styles.visible : styles.hidden]}>
                Not registered?{' '}
                <Pressable onPress={() => navigation.navigate('Signup')} disabled={state.isAdminMode}>
                  <Text style={dynamicStyles.link}>Create an account</Text>
                </Pressable>
              </Text>

              {/* Footer text - Only show for residents */}
              <Text style={[styles.footerText, !state.isAdminMode ? styles.visible : styles.hidden]}>
                By signing in, you agree to our{' '}
                <Pressable onPress={onTermsOfService} disabled={state.isAdminMode}>
                  <Text style={dynamicStyles.link}>Terms of Service</Text>
                </Pressable>{' '}
                and{' '}
                <Pressable onPress={onPrivacyPolicy} disabled={state.isAdminMode}>
                  <Text style={dynamicStyles.link}>Privacy Policy</Text>
                </Pressable>
              </Text>
            </View>

            <Pressable accessibilityRole="button" onPress={state.submit} style={{ display: 'none' }} />
          </View>
        </View>
      </View>

      {/* Legal Modal - Only renders on web */}
      <LegalModal
        visible={modalVisible}
        contentType={modalContentType}
        onClose={closeModal}
      />
    </View>
  );
};

export const LoginDesktopContent: React.FC = () => {
  const navigation = useNavigation<any>();
  const state = useLoginForm();
  const { showLegalContent, modalVisible, modalContentType, closeModal } = useLegalContent();
  const styles = useThemedStyles(makeStyles);

  const onTermsOfService = () => showLegalContent('terms');
  const onPrivacyPolicy = () => showLegalContent('privacy');

  const title = state.isAdminMode ? 'Welcome to Resio Community Portal!' : 'Welcome to Resio!';
  const link = [styles.link, state.isAdminMode ? styles.linkAdmin : styles.linkResident];
  const signupRow = [styles.signupRow, state.isAdminMode ? styles.signupRowAdmin : styles.signupRowResident];
  const conditionalContent = [styles.conditionalContent, !state.isAdminMode && styles.conditionalContentVisible];

  return (
    <View style={desktopContentStyles.formInner}>
      <View style={styles.titleContainer}>
        <Text style={[styles.title, styles.titleDesktop, state.isAdminMode ? styles.titleAdmin : styles.titleResident]}>
          {title}
        </Text>
      </View>

      <LoginForm state={state} autoFocusEmail />

      <View style={styles.linksRow}>
        <Text style={styles.spacer} />
        <Pressable onPress={() => navigation.navigate('ForgotPassword', { email: state.email })}>
          <Text style={link}>Forgotten Password?</Text>
        </Pressable>
      </View>

      <View style={conditionalContent}>
        <Text style={[signupRow, !state.isAdminMode ? styles.visible : styles.hidden]}>
          Not registered?{' '}
          <Pressable onPress={() => navigation.navigate('Signup')} disabled={state.isAdminMode}>
            <Text style={link}>Create an account</Text>
          </Pressable>
        </Text>

        <Text style={[styles.footerText, !state.isAdminMode ? styles.visible : styles.hidden]}>
          By signing in, you agree to our{' '}
          <Pressable onPress={onTermsOfService} disabled={state.isAdminMode}>
            <Text style={link}>Terms of Service</Text>
          </Pressable>{' '}
          and{' '}
          <Pressable onPress={onPrivacyPolicy} disabled={state.isAdminMode}>
            <Text style={link}>Privacy Policy</Text>
          </Pressable>
        </Text>
      </View>

      <Pressable accessibilityRole="button" onPress={state.submit} style={{ display: 'none' }} />

      <LegalModal visible={modalVisible} contentType={modalContentType} onClose={closeModal} />
    </View>
  );
};

const desktopContentStyles = StyleSheet.create({
  formInner: {
    width: '100%',
    maxWidth: 512,
    paddingHorizontal: 32,
    paddingTop: 40,
    paddingBottom: 40,
    minHeight: '100%' as any,
    display: 'flex' as any,
    flexDirection: 'column' as any,
    justifyContent: 'center' as any,
  },
});

const makeStyles = ({ colors, semantic }: ThemeContextValue) => StyleSheet.create({
  // Base styles
  pageWrapper: {
    flex: 1,
    ...(Platform.OS === 'web' && { minHeight: '100vh' as any }),
  },
  // Header styles - matching web apps (p-4 = 16px padding)
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16, // p-4 equivalent
    borderBottomWidth: 1,
    borderBottomColor: semantic.border.secondary, // gray-700
    backgroundColor: 'transparent',
    ...Platform.select({
      web: {
        position: 'sticky' as any,
        top: 0,
        zIndex: 10,
      },
    }),
  },
  logoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  logoIcon: {
    marginRight: 8, // mr-2 equivalent
    // For unauthenticated state: h-16 w-16 (64px × 64px)
    width: 64,
    height: 64,
  },
  logoText: {
    // For unauthenticated state: text-5xl (48px)
    fontSize: Platform.select({ web: 48, default: 32 }),
    fontWeight: '600', // font-semibold
    color: colors.text.primary, // White text for dark theme
    letterSpacing: 0.5,
  },
  // Admin-specific page wrapper
  pageWrapperAdmin: {
    backgroundColor: colors.bg.base, // Dark background for admin
  },
  // Resident-specific page wrapper
  pageWrapperResident: {
    backgroundColor: colors.bg.base, // Dark background for resident
  },
  splitContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderColor: semantic.border.secondary, // gray-700
    borderBottomWidth: Platform.select({ web: 1, default: 0 }),
    ...(Platform.OS === 'web' && { maxWidth: 1200 }),
    alignSelf: 'center',
    width: '100%',
  },
  // Mobile: Stack vertically, no image
  splitContainerMobile: {
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: 'flex-start',
    maxWidth: '100%',
  },
  // Tablet: Still stack vertically but with more space
  splitContainerTablet: {
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    maxWidth: 768,
  },
  // Desktop: Side by side layout
  splitContainerDesktop: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    maxWidth: 1200,
  },
  imagePane: {
    ...Platform.select({
      web: {
        display: 'flex' as any,
        width: 384,
        height: 'calc(100vh - 97px)' as any,
        flexShrink: 0,
        overflow: 'hidden', // Prevent image from causing shifts
        position: 'relative' as any, // Needed for absolute positioned image
      },
      default: {
        display: 'none',
      },
    }),
  },
  // Mobile: Hidden
  imagePaneMobile: {
    display: 'none',
  },
  // Tablet: Hidden (following original web app pattern)
  imagePaneTablet: {
    display: 'none',
  },
  // Desktop: Visible with fixed width
  imagePaneDesktop: {
    display: 'flex' as any,
    width: 384,
    height: 'calc(100vh - 97px)' as any,
    flexShrink: 0,
    overflow: 'hidden', // Prevent image from causing shifts
    position: 'relative' as any, // Needed for absolute positioned image
  },
  image: {
    width: '100%',
    height: '100%',
    ...(Platform.OS === 'web' && { 
      objectFit: 'cover' as any,
      position: 'absolute' as any, // Absolute positioning prevents layout shift
      top: 0,
      left: 0,
    }),
  },
  formPane: {
    width: 512,
    alignItems: 'center',
    justifyContent: Platform.select({ web: 'flex-start', default: 'flex-start' }),
    borderColor: semantic.border.secondary,
    ...Platform.select({
      web: {
        borderLeftWidth: 1,
        borderRightWidth: 1,
        height: 'calc(100vh - 97px)' as any,
        flexShrink: 0,
        overflow: 'auto' as any,
      },
      default: {
        flex: 1,
        borderLeftWidth: 1,
        borderRightWidth: 1,
      },
    }),
  },
  // Mobile: Full width, no borders, padding for mobile
  formPaneMobile: {
    width: '100%',
    borderLeftWidth: 0,
    borderRightWidth: 0,
    height: 'auto' as any,
    justifyContent: 'flex-start',
    paddingHorizontal: 16,
  },
  // Tablet: Constrained width, centered
  formPaneTablet: {
    width: '100%',
    maxWidth: 512,
    borderLeftWidth: 0,
    borderRightWidth: 0,
    height: 'auto' as any,
    justifyContent: 'center',
    paddingHorizontal: 32,
  },
  // Desktop: Fixed width with borders
  formPaneDesktop: {
    width: 512,
    borderLeftWidth: 1,
    borderRightWidth: 1,
    height: 'calc(100vh - 97px)' as any,
    justifyContent: 'flex-start',
    flexShrink: 0,
    overflow: 'auto' as any,
  },
  // Admin-specific form pane
  formPaneAdmin: {
    backgroundColor: colors.bg.base, // Dark background
  },
  // Resident-specific form pane
  formPaneResident: {
    backgroundColor: colors.bg.base, // Dark background
  },
  formInner: {
    width: '100%',
    maxWidth: 512,
    paddingHorizontal: 32,
    paddingVertical: Platform.select({ web: 40, default: 24 }),
    ...Platform.select({
      web: {
        paddingTop: 40,
        paddingBottom: 40,
        minHeight: '100%' as any,
        display: 'flex' as any,
        flexDirection: 'column' as any,
        justifyContent: 'center' as any,
      },
    }),
  },
  // Mobile: Reduced padding, full width
  formInnerMobile: {
    paddingHorizontal: 16,
    paddingVertical: 24,
    maxWidth: '100%',
  },
  // Tablet: Medium padding
  formInnerTablet: {
    paddingHorizontal: 24,
    paddingVertical: 32,
    maxWidth: 480,
  },
  // Desktop: Original padding
  formInnerDesktop: {
    paddingHorizontal: 32,
    paddingVertical: 40,
    maxWidth: 512,
    minHeight: '100%' as any,
    display: 'flex' as any,
    flexDirection: 'column' as any,
    justifyContent: 'center' as any,
  },
  titleContainer: {
    // Fixed minimum height to accommodate both title variants (2 lines max)
    // Line height 36px * 2 lines = 72px, plus buffer for safety
    minHeight: Platform.select({ web: 80, default: 68 }),
    justifyContent: 'flex-start',
    marginBottom: 24,
    marginTop: 24,
  },
  title: {
    fontSize: Platform.select({ web: 30, default: 24 }),
    fontWeight: '600',
    textAlign: Platform.select({ web: 'left', default: 'center' }),
    lineHeight: Platform.select({ web: 36, default: 30 }),
    // Prevent overflow and ensure consistent wrapping
    flexWrap: 'wrap',
  },
  // Mobile: Center aligned, smaller font
  titleMobile: {
    fontSize: 24,
    textAlign: 'center',
    lineHeight: 30,
    // marginBottom removed - controlled by titleContainer
  },
  titleTablet: {
    fontSize: 24,
    textAlign: 'center',
    lineHeight: 30,
  },
  // Desktop: Left aligned, larger font (matching web apps)
  titleDesktop: {
    fontSize: 30,
    textAlign: 'left',
    lineHeight: 36,
  },
  // Admin-specific title
  titleAdmin: {
    color: colors.text.primary, // White text for dark theme
  },
  // Resident-specific title
  titleResident: {
    color: colors.text.primary, // White text for dark theme
  },
  subtitle: {
    fontSize: 14,
    textAlign: 'center',
    color: colors.text.muted,
    marginBottom: 16,
  },
  linksRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginTop: 8,
    marginBottom: 8,
  },
  spacer: {
    flex: 1,
  },
  signupRow: {
    textAlign: 'center',
    fontSize: 14,
    marginTop: 16,
    fontWeight: '500',
  },
  // Mobile: Additional top padding (matching resio-web max-sm:pt-4)
  signupRowMobile: {
    marginTop: 20, // pt-4 equivalent
  },
  // Admin-specific signup row (not used but kept for consistency)
  signupRowAdmin: {
    color: colors.text.primary, // White text
  },
  // Resident-specific signup row
  signupRowResident: {
    color: colors.text.primary, // White text
  },
  footerText: {
    fontSize: 12,
    color: colors.text.muted,
    textAlign: 'center',
    marginTop: 16,
  },
  link: {
    textDecorationLine: 'underline',
    fontSize: 14,
    fontWeight: '500',
  },
  // Admin-specific link
  linkAdmin: {
    color: '#f55560', // intentional brand pink link (same in light & dark)
  },
  // Resident-specific link
  linkResident: {
    color: '#f55560', // intentional brand pink link (same in light & dark)
  },
  // Conditional content container - Always takes up space
  conditionalContent: {
    minHeight: 0,
    opacity: 0,
    overflow: 'hidden',
  },
  conditionalContentVisible: {
    minHeight: 'auto' as any,
    opacity: 1,
    overflow: 'visible',
  },
  // Visibility helpers
  visible: {
    opacity: 1,
  },
  hidden: {
    opacity: 0,
    ...(Platform.OS === 'web' && {
      pointerEvents: 'none' as any,
    }),
  },
});


